/**
 * Brand Constants — config-driven via lib/client/config.ts
 *
 * IMPORTANT: Export signatures are IMMUTABLE. 181 files depend on
 * COLORS, FONTS, SPACING, RADIUS having exact same shape.
 * Only change WHERE values come from, never WHAT they are.
 */
import { getClientConfig } from './client/config'

const config = getClientConfig()

// ── Brand Identity ──────────────────────────────────────────────────────────

export const BRAND = {
  name: config.brand.name,
  meaning: config.brand.meaning,
  tagline: config.brand.tagline,
  service: config.brand.service,
  version: config.brand.version,
  contact: config.brand.contact,
} as const

// ── Colors ──────────────────────────────────────────────────────────────────

export const COLORS = {
  // Primary brand colors (named for Tago compat — maps to config.colors.primary/secondary/accent)
  purple: config.colors.primary,
  coral: config.colors.secondary,
  amber: config.colors.accent,

  // Semantic aliases
  primary: config.colors.primary.DEFAULT,
  secondary: config.colors.secondary.DEFAULT,
  accent: config.colors.accent.DEFAULT,

  // Gradient
  gradient: config.colors.gradient,

  // Neutral
  white: '#FFFFFF' as const,
  black: '#000000' as const,

  // Background variations (derived from primary)
  background: {
    primary: config.colors.primary.DEFAULT,
    secondary: config.colors.primary.dark,
    tertiary: config.colors.primary.light,
    card: 'rgba(255, 255, 255, 0.10)' as const,
    cardBorder: 'rgba(255, 255, 255, 0.12)' as const,
  },

  // Text colors (universal — not brand-specific)
  text: {
    primary: '#FFFFFF' as const,
    secondary: 'rgba(255, 255, 255, 0.75)' as const,
    muted: 'rgba(255, 255, 255, 0.5)' as const,
    disabled: 'rgba(255, 255, 255, 0.3)' as const,
    dark: config.colors.primary.DEFAULT,
    darkSecondary: `rgba(${parseInt(config.colors.primary.DEFAULT.slice(1, 3), 16)}, ${parseInt(config.colors.primary.DEFAULT.slice(3, 5), 16)}, ${parseInt(config.colors.primary.DEFAULT.slice(5, 7), 16)}, 0.7)`,
  },

  // Status colors
  success: config.colors.status.success,
  warning: config.colors.status.warning,
  error: config.colors.status.error,
  info: config.colors.status.info,

  // Condition grade colors (check-in/check-out inspection)
  condition: {
    excellent: '#059669' as const,
    good: '#2563EB' as const,
    fair: '#D97706' as const,
    poor: '#DC2626' as const,
  },

  // National flag colors (Sept 15 Independence Day campaign)
  flag: {
    blue: '#0F4EA1' as const,
    blueSoft: '#8DC4FF' as const,
    gold: '#D9A321' as const,
  },
} as const

// ── Typography ──────────────────────────────────────────────────────────────

export const FONTS = {
  heading: config.fonts.heading,
  body: config.fonts.body,
  bodyBold: config.fonts.bodyBold,
  mono: config.fonts.mono,

  weights: {
    light: '300' as const,
    regular: '400' as const,
    medium: '500' as const,
    semibold: '600' as const,
    bold: '700' as const,
  },

  sizes: {
    xs: 11,
    sm: 13,
    base: 15,
    md: 17,
    lg: 20,
    xl: 24,
    '2xl': 32,
    '3xl': 40,
    '4xl': 48,
  },
} as const

// ── Spacing ─────────────────────────────────────────────────────────────────

export const SPACING = config.spacing as {
  readonly xs: number
  readonly sm: number
  readonly md: number
  readonly lg: number
  readonly xl: number
  readonly '2xl': number
  readonly '3xl': number
}

// ── Radius ──────────────────────────────────────────────────────────────────

export const RADIUS = config.radius as {
  readonly xs: number
  readonly sm: number
  readonly md: number
  readonly lg: number
  readonly xl: number
  readonly full: number
}

// ── Gradients (derived from config colors) ──────────────────────────────────

export const GRADIENTS = {
  primary: {
    colors: [config.colors.gradient.start, config.colors.gradient.end] as const,
    start: { x: 0, y: 0 },
    end: { x: 1, y: 1 },
  },
  primarySmooth: {
    colors: [
      config.colors.gradient.start,
      config.colors.gradient.middle,
      config.colors.gradient.end,
    ] as const,
    start: { x: 0, y: 0 },
    end: { x: 1, y: 1 },
  },
  vertical: {
    colors: [config.colors.gradient.start, config.colors.gradient.end] as const,
    start: { x: 0.5, y: 0 },
    end: { x: 0.5, y: 1 },
  },
  horizontal: {
    colors: [config.colors.gradient.start, config.colors.gradient.end] as const,
    start: { x: 0, y: 0.5 },
    end: { x: 1, y: 0.5 },
  },
  cardOverlay: {
    colors: [
      `rgba(${parseInt(config.colors.primary.DEFAULT.slice(1, 3), 16)}, ${parseInt(config.colors.primary.DEFAULT.slice(3, 5), 16)}, ${parseInt(config.colors.primary.DEFAULT.slice(5, 7), 16)}, 0.8)`,
      `rgba(${parseInt(config.colors.primary.DEFAULT.slice(1, 3), 16)}, ${parseInt(config.colors.primary.DEFAULT.slice(3, 5), 16)}, ${parseInt(config.colors.primary.DEFAULT.slice(5, 7), 16)}, 0.4)`,
    ] as const,
    start: { x: 0, y: 0 },
    end: { x: 0, y: 1 },
  },
} as const
